import React, { useEffect, useRef } from 'react';
import { useDrag, useDrop, useDragLayer } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { createPortal } from 'react-dom';
import { t } from '@lingui/macro';
import { ListItem, ListItemIcon, IconButton } from '@material-ui/core';
import DragHandleIcon from '@material-ui/icons/DragHandle';
import DeleteIcon from '@material-ui/icons/Delete';
import { useDeleteDialog } from '../../form/DeleteDialog';
import { usePollingStyles } from './PollingStyles';

export const CREDENTIAL_ITEM_TYPE = 'credential';

function CredentialRowContent({ description, type }) {
    const classes = usePollingStyles();
    return (
        <>
            <div className={classes.dragHandle}>
                <ListItemIcon>
                    <DragHandleIcon />
                </ListItemIcon>
            </div>
            <div className={classes.credentialText}>
                <div className={classes.credentialTextTitle}>{description}</div>
                <div className={classes.credentialTextDesc}>{type}</div>
            </div>
            <DeleteIcon fontSize="small" color="action" />
        </>
    );
}

export function CredentialDragPreview() {
    const classes = usePollingStyles();
    const { isDragging, itemType, item, sourceOffset } = useDragLayer((monitor) => ({
        isDragging: monitor.isDragging(),
        itemType: monitor.getItemType(),
        item: monitor.getItem(),
        sourceOffset: monitor.getSourceClientOffset(),
    }));

    if (!isDragging || itemType !== CREDENTIAL_ITEM_TYPE || !item || !sourceOffset) {
        return null;
    }

    return createPortal(
        <div
            className={classes.credentialDragPreview}
            style={{
                left: sourceOffset.x,
                top: sourceOffset.y,
                width: item.width,
                opacity: 0.45,
            }}
        >
            <CredentialRowContent description={item.description} type={item.credentialType} />
        </div>,
        document.body
    );
}

/**
 * @param {{
 *   cred: any;
 *   index: number;
 *   move: (from: number, to: number) => void;
 *   remove: (index: number) => void;
 *   onEdit: (cred: any, index: number) => void;
 * }} props
 */
export default function CredentialRow({ cred, index, move, remove, onEdit }) {
    const classes = usePollingStyles();
    const boundsRef = useRef(null);
    const [deleteDialog, showDeleteDialog] = useDeleteDialog(t`Are you sure you want to delete this credential?`);

    const [{ isDragging }, dragRef, preview] = useDrag({
        type: CREDENTIAL_ITEM_TYPE,
        item: () => {
            const width = boundsRef.current ? boundsRef.current.offsetWidth : 300;
            return { index, description: cred.description, credentialType: cred.type, width };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [, dropRef] = useDrop({
        accept: CREDENTIAL_ITEM_TYPE,
        hover(item) {
            if (!boundsRef.current) return;
            if (item.index === index) return;
            move(item.index, index);
            item.index = index;
        },
    });

    useEffect(() => {
        preview(getEmptyImage(), { captureDraggingState: true });
    }, [preview]);

    function attachRowRefs(el) {
        boundsRef.current = el;
        dropRef(el);
    }

    return (
        <>
            <div ref={attachRowRefs} style={{ opacity: isDragging ? 0 : 1 }}>
                <ListItem divider button className={classes.credentialListItem}>
                    {/* Drag Handle */}
                    <div ref={dragRef} className={classes.dragHandle} title="Click and drag to reorder">
                        <ListItemIcon>
                            <DragHandleIcon />
                        </ListItemIcon>
                    </div>
                    <div
                        className={classes.credentialText}
                        onClick={() => onEdit(cred, index)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onEdit(cred, index);
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        <div className={classes.credentialTextTitle}>{cred.description}</div>
                        <div className={classes.credentialTextDesc}>{cred.type}</div>
                    </div>

                    {/* Delete */}
                    <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={(e) => {
                            e.stopPropagation();
                            showDeleteDialog(() => remove(index));
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </ListItem>
            </div>
            {deleteDialog}
        </>
    );
}
